'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { User } from '../../types';
import {
  Menu,
  Bell,
  Search,
  ChevronDown,
  LogOut,
  User as UserIcon,
  Settings,
} from 'lucide-react';

interface HeaderProps {
  user: User;
  onToggleSidebar: () => void;
}

export function Header({ user, onToggleSidebar }: HeaderProps) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // 데모용 알림
  const notifications = [
    { id: 1, title: '새 주문이 들어왔습니다', time: '5분 전', read: false },
    { id: 2, title: '재고 부족 알림: 상품 A', time: '30분 전', read: false },
    { id: 3, title: '결제가 완료되었습니다', time: '1시간 전', read: true },
  ];

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <header className="h-14 sm:h-16 bg-card border-b border-border flex items-center justify-between px-3 sm:px-4">
      {/* 왼쪽: 메뉴 토글 & 검색 */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* 햄버거 메뉴 - 모바일에서만 */}
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg hover:bg-accent transition-colors lg:hidden"
          aria-label="메뉴 열기"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* 모바일 로고 */}
        <div className="flex items-center gap-2 lg:hidden">
          <span className="font-bold text-sm">Box ERP</span>
        </div>

        {/* 검색 - 태블릿 이상에서만 */}
        <div className="hidden md:flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="검색..."
            className="bg-transparent border-none outline-none text-sm w-48 lg:w-64 placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* 오른쪽: 알림 & 사용자 */}
      <div className="flex items-center gap-2">
        {/* 알림 */}
        <div className="relative">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowUserMenu(false);
            }}
            className="relative p-2 rounded-lg hover:bg-accent transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {/* 알림 드롭다운 */}
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50">
              <div className="p-3 border-b border-border">
                <h3 className="font-semibold">알림</h3>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={cn(
                      'p-3 border-b border-border last:border-0 hover:bg-accent cursor-pointer',
                      !notification.read && 'bg-accent/50'
                    )}
                  >
                    <p className="text-sm font-medium">{notification.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {notification.time}
                    </p>
                  </div>
                ))}
              </div>
              <div className="p-2 border-t border-border">
                <button className="w-full text-center text-sm text-primary hover:underline py-1">
                  모든 알림 보기
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 구분선 */}
        <div className="w-px h-6 bg-border mx-2" />

        {/* 사용자 메뉴 */}
        <div className="relative">
          <button
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent transition-colors"
          >
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-sm font-medium text-primary-foreground">
                  {user.name.charAt(0)}
                </span>
              )}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium">{user.name}</p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-muted-foreground hidden md:block" />
          </button>

          {/* 사용자 드롭다운 */}
          {showUserMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-lg shadow-lg z-50">
              <div className="p-3 border-b border-border">
                <p className="font-medium">{user.name}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
              <div className="py-1">
                <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors">
                  <UserIcon className="w-4 h-4" />
                  내 프로필
                </button>
                <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors">
                  <Settings className="w-4 h-4" />
                  설정
                </button>
              </div>
              <div className="border-t border-border py-1">
                <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-accent transition-colors">
                  <LogOut className="w-4 h-4" />
                  로그아웃
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 클릭 외부 감지용 오버레이 */}
      {(showUserMenu || showNotifications) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            setShowNotifications(false);
          }}
        />
      )}
    </header>
  );
}
